/**
 * Notification Service — Frontend
 *
 * In-memory cache of notifications, driven by the SSE notifications channel.
 * This service is the presenter layer: components subscribe here for UI updates.
 *
 * Flow:
 *   Backend creates Notification → notification_sse_bus.notify()
 *     → notificationsSSEService fires callback → notificationService.upsert()
 *     → subscribers (NotificationTray) re-render
 *
 * Read state:
 *   markRead(id) → in-memory update → BroadcastChannel sync to all tabs
 *                                   → _persistReadState for MongoDB durability
 */

import { createFileLogger } from '../utils/logger';

const log = createFileLogger('NOTIFICATION_SERVICE');

export interface AppNotification {
  id: string;
  type: 'automation_complete' | 'automation_error';
  title: string;
  message: string;
  jobId?: string;
  playbookId?: string;
  playbookRunId?: string;
  actionUrl?: string;
  status: 'running' | 'succeeded' | 'failed';
  result?: any;
  read: boolean;
  timestamp: number;
}

// Backward-compat alias — remove after all callers updated
export type PlaybookNotification = AppNotification;

type NotificationListener = (notification: AppNotification) => void;

export class NotificationService {
  private initialized = false;
  private notifications: AppNotification[] = [];
  private listeners: Set<NotificationListener> = new Set();
  private _bc: BroadcastChannel | null = null;

  init() {
    if (this.initialized) return;
    log.info('[NotificationService] Initializing');
    window.addEventListener('message', this.handleMessage.bind(this));
    // Cross-tab read-state sync via BroadcastChannel (replaces Y.js BroadcastChannel)
    if (typeof BroadcastChannel !== 'undefined') {
      this._bc = new BroadcastChannel('river_notifications_read_state');
      this._bc.onmessage = (e) => {
        if (e.data?.type === 'mark_read') {
          const n = this.notifications.find(n => n.id === e.data.id);
          if (n) { n.read = true; this._notifyAll(); }
        } else if (e.data?.type === 'mark_all_read') {
          this.notifications.forEach(n => { n.read = true; });
          this._notifyAll();
        }
      };
    }
    this.initialized = true;
  }

  /**
   * Add or update a notification.
   * Called by the Y.Doc observer in App.tsx when the Y.Map changes.
   * Handles both new notifications (action='add') and read-state updates (action='update').
   */
  upsert(notification: Omit<AppNotification, 'timestamp'> & { timestamp?: number }) {
    // log.debug('[NotificationService] upsert:', notification.id, 'read=', notification.read);
    const existing = this.notifications.find(n => n.id === notification.id);
    if (existing) {
      // Update in place — preserve timestamp, update everything else (especially read)
      Object.assign(existing, { ...notification, timestamp: existing.timestamp });
    } else {
      this.notifications.unshift({
        ...notification,
        timestamp: notification.timestamp ?? Date.now(),
      } as AppNotification);
      // Keep sorted newest-first
      this.notifications.sort((a, b) => b.timestamp - a.timestamp);
    }
    this._notifyAll();
  }

  /**
   * Emit a transient notification (no backing entity — for in-app use only).
   * Deprecated for backend automation notifications; use upsert() instead.
   */
  emit(notification: Omit<AppNotification, 'id' | 'read' | 'timestamp'> & { id?: string; read?: boolean }) {
    const id = notification.id ?? `notification_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    if (this.notifications.some(n => n.id === id)) return;
    const full: AppNotification = {
      ...notification,
      id,
      read: notification.read ?? false,
      timestamp: Date.now(),
    };
    this.notifications.unshift(full);
    // Also fire a popup toast so playbook completions get banners
    this.showToast({
      message: notification.message,
      priority: notification.status === 'failed' ? 'high' : 'medium',
      actionUrl: notification.actionUrl,
    });
    this._notifyAll();
  }

  getAll(): AppNotification[] {
    return [...this.notifications];
  }

  getUnreadCount(): number {
    return this.notifications.filter(n => !n.read).length;
  }

  subscribe(listener: NotificationListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  markRead(id: string) {
    const n = this.notifications.find(n => n.id === id);
    if (n) n.read = true;
    this._persistReadState(id, true);
    this._bcBroadcast({ type: 'mark_read', id });
    this._notifyAll();
  }

  markAllRead() {
    const ids: string[] = [];
    this.notifications.forEach(n => {
      if (!n.read) {
        n.read = true;
        ids.push(n.id);
        this._persistReadState(n.id, true);
      }
    });
    if (ids.length > 0) {
      this._bcBroadcast({ type: 'mark_all_read' });
    }
    this._notifyAll();
  }

  clear() {
    this.notifications = [];
    this._notifyAll();
  }

  /**
   * Handle backend WebSocket notification (if websocket_manager is ever wired in).
   * Falls through to upsert() via the Y.Doc observer path normally.
   */
  async dispatch(notification: {
    channel: string;
    message: string;
    priority?: string;
    duration?: number;
    entityId?: string;
    [key: string]: any;
  }) {
    log.debug('[NotificationService] dispatch:', notification.channel);
    // Entity-backed notifications arrive via Y.Doc observer → upsert().
    // Only show DOM toast for ephemeral notifications without a backing entity.
    if (!notification.entityId && notification.channel === 'toast') {
      this.showToast(notification);
    }
  }

  private _bcBroadcast(msg: object): void {
    try { this._bc?.postMessage(msg); } catch { /* ignore */ }
  }

  private _notifyAll() {
    const first = this.notifications[0] as any;
    this.listeners.forEach(fn => fn(first));
  }

  /**
   * Patch skeleton.read on the Notification document in MongoDB.
   * Cross-tab sync is handled by Y.js BroadcastChannel before this resolves.
   */
  private _persistReadState(entityId: string, read: boolean) {
    // Only entity-backed notifications have a MongoDB ObjectId (24 hex chars)
    if (entityId.startsWith('notification_') || entityId.startsWith('toast_')) return;
    import('../api/config').then(({ API_BASE_URL }) => {
      import('./fetchService').then(({ default: fetchService }) => {
        fetchService(`${API_BASE_URL}/notifications/${entityId}`, {
          method: 'PUT',
          body: JSON.stringify({ skeleton: { read } }),
        }).catch(err => {
          log.warn('[NotificationService] Failed to persist read state:', err);
        });
      });
    });
  }

  /** Show a toast from a notifications SSE event payload (type=toast). */
  showToastFromEntry(entry: { message: string; priority?: string; duration?: number; actionUrl?: string; data?: any }) {
    log.debug('[NotificationService] Showing toast from SSE entry');
    this.showToast(entry);
  }

  private showToast(notification: any) {
    log.debug('[NotificationService] Showing toast');
    import('../stores/toastStore').then(({ toastStore }) => {
      toastStore.add({
        message: notification.message,
        priority: notification.priority,
        duration: notification.duration,
        actionUrl: notification.actionUrl,
        data: notification.data,
      });
    });
  }

  private async handleMessage(event: MessageEvent) {
    if (event.data?.type !== 'notification') return;
    await this.dispatch(event.data);
  }
}

// Singleton
export const notificationService = new NotificationService();

if (typeof window !== 'undefined') {
  notificationService.init();
}
